import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import { CommunityInvite, FRIENDS_LIST_PATH, friendInvitePath } from '@radevents/shared';
import PublicHeader from '../components/Layout/PublicHeader';
import AppTopbarBrand from '../components/Layout/AppTopbarBrand';
import { FriendInviteView } from '../components/community/FriendInviteView';
import { Spinner } from '../components/ui';
import { useAuth } from '../contexts/AuthContext';
import { clearListCache } from '../hooks/useListCache';
import service from '../services/communityService';

/**
 * `/freunde/einladung/:code`: persönlicher Einladungslink. Öffentlich lesbar, damit auch jemand
 * ohne Konto sieht, worum es geht. „Anmelden und Einladung annehmen“ ist schon die Zusage: die Anmeldung
 * führt mit `?annehmen=1` hierher zurück, und die Einladung wird ohne zweiten Klick angenommen.
 * Danach geht es in die Freundesliste.
 */
export default function FriendInvite() {
  const { code = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const acceptAfterLogin = params.get('annehmen') === '1';
  const autoAccepted = useRef(false);
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, user, logout } = useAuth();
  const [invite, setInvite] = useState<CommunityInvite | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isLoading) return;
    let active = true;
    setLoaded(false); setError('');
    service.invite(code)
      .then(data => { if (active) setInvite(data); })
      .catch(() => { if (active) setInvite(null); })
      .finally(() => { if (active) setLoaded(true); });
    return () => { active = false; };
  }, [code, isLoading, user?.id]);

  const accept = async () => {
    setBusy(true); setError('');
    try {
      const result = await service.acceptInvite(code);
      clearListCache('landing'); clearListCache('favorites');
      // Weiter zur Freundesliste, dort steht die neue Freundschaft.
      toast.success(`Du bist jetzt mit ${result.firstName} befreundet`);
      navigate(FRIENDS_LIST_PATH, { replace: true });
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Die Einladung konnte nicht angenommen werden. Bitte versuche es erneut.');
    } finally { setBusy(false); }
  };
  const login = () => navigate(`/login?redirect=${encodeURIComponent(`${friendInvitePath(code)}?annehmen=1`)}`);
  // Abmelden und mit einem anderen Konto anmelden; die Einladung wird danach wie bei Gästen angenommen.
  const switchAccount = async () => { await logout(); login(); };

  useEffect(() => {
    if (!acceptAfterLogin || !loaded || !isAuthenticated || autoAccepted.current) return;
    autoAccepted.current = true;
    setParams({}, { replace: true });
    if (invite && !invite.own && invite.state !== 'FRIEND') void accept();
  // accept liest nur code; der Effekt soll genau einmal nach dem Laden laufen.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acceptAfterLogin, loaded, isAuthenticated, invite]);

  return <div style={{ background: '#F5F3F3', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
    <Helmet>
      <title>Einladung | Radsport Events</title>
      <meta name="robots" content="noindex" />
    </Helmet>
    <div className="hide-mobile"><PublicHeader /></div>
    <div className="app-topbar hide-desktop"><AppTopbarBrand onBack={() => navigate(isAuthenticated ? '/favorites?view=friends' : '/')} /></div>
    <main className="friend-invite-page">
      {!loaded || (acceptAfterLogin && isAuthenticated) ? <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}><Spinner /></div> : <FriendInviteView invite={invite} busy={busy} error={error} account={user?.email || user?.username} onAccept={accept} onLogin={login} onSwitchAccount={() => { void switchAccount(); }} />}
    </main>
  </div>;
}
